import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  Button,
  IconButton
} from '@mui/material'
import DownloadIcon from '@mui/icons-material/Download'
import ClearIcon from '@mui/icons-material/Clear'
import { useState } from 'react'
import { SelectWithLabel, type SelectOption } from '../../Components/SelectWithLabel'
import { type AggregationLevel, type GSRPCapacityReportModel } from './GSRPCapacityReport.types'
import { aggregateReportData } from './GSRPCapacityReport.utils'
import * as XLSX from 'xlsx'

interface TableViewProps {
  reportData: GSRPCapacityReportModel[]
  providers: Array<SelectOption<number>>
  providerID: number | string
  setProviderID: (id: number | string) => void
  isdID: number | undefined
  loading: boolean
}

export function GSRPCapacityReportTableView (props: TableViewProps): JSX.Element {
  const { reportData, providers, providerID, setProviderID, isdID, loading } = props
  const [aggregationLevel, setAggregationLevel] = useState<AggregationLevel>('facility')

  // Aggregate data based on selected level
  const aggregatedData = aggregateReportData(reportData, aggregationLevel)

  // Determine row color based on capacity
  const getRowColor = (totalActive: number, capacity: number): string => {
    if (capacity === 0) return '#F1F8F1' // Light green for no capacity

    const utilizationPercent = (totalActive / capacity) * 100
    if (utilizationPercent >= 100) {
      return '#FDEBEC' // Very light red - full capacity
    } else if (utilizationPercent >= 80) {
      return '#FFF9E1' // Very light yellow - within 20% of capacity
    }
    return '#F1F8F1' // Light green - less than 80%
  }

  // Export to Excel
  const handleExportToExcel = (): void => {
    const headers: string[] = ['Provider Name']
    if (aggregationLevel !== 'provider') {
      headers.push('Facility Name')
    }
    headers.push('Pending', 'Enrolled', 'Waitlisted', 'Active Seats', 'Capacity')

    const data = aggregatedData.map(row => {
      const rowData: Array<string | number> = [row.providerName]
      if (aggregationLevel !== 'provider') {
        rowData.push(row.facilityName ?? '')
      }
      rowData.push(
        row.countPendingProcessing,
        row.countEnrolled,
        row.countWaitlisted,
        row.totalActiveSeats,
        row.totalAllocatedCapacity
      )
      return rowData
    })

    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...data])
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'GSRP Capacity Report')

    const date = new Date().toISOString().split('T')[0]
    XLSX.writeFile(workbook, `GSRP_Capacity_Report_${aggregationLevel}_${date}.xlsx`)
  }

  return (
    <Box>
      {/* Controls */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Provider filter */}
          <Box sx={{ width: 250 }}>
            <SelectWithLabel
              name="providerID"
              label="Provider Name"
              value={providerID}
              width="250px"
              options={providers}
              onChange={(e) => { setProviderID(e.target.value) }}
              disabled={isdID === undefined || loading}
              SelectProps={{
                sx: {
                  '& .MuiSelect-select': {
                    pr: providerID !== '' ? '40px !important' : undefined
                  }
                },
                endAdornment: providerID !== '' && (
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation()
                      setProviderID('')
                    }}
                    aria-label="Clear provider filter"
                    sx={{
                      position: 'absolute',
                      right: '32px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      zIndex: 1,
                      p: 0.5,
                      color: 'rgba(0, 0, 0, 0.54)',
                      '&:hover': {
                        color: 'rgba(0, 0, 0, 0.87)',
                        bgcolor: 'transparent'
                      }
                    }}
                  >
                    <ClearIcon sx={{ fontSize: '18px' }} />
                  </IconButton>
                )
              }}
            />
          </Box>

          {/* View by toggle */}
          {reportData.length > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                View by:
              </Typography>
              <ToggleButtonGroup
                value={aggregationLevel}
                exclusive
                onChange={(e, newLevel) => {
                  if (newLevel !== null) {
                    setAggregationLevel(newLevel)
                  }
                }}
                size="small"
                aria-label="Aggregation level"
              >
                <ToggleButton value="facility">Facility</ToggleButton>
                <ToggleButton value="provider">Provider</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          )}
        </Box>

        {/* Download button */}
        {reportData.length > 0 && (
          <Button
            variant="contained"
            size="medium"
            startIcon={<DownloadIcon />}
            onClick={handleExportToExcel}
            sx={{ bgcolor: '#367575', '&:hover': { bgcolor: '#2A5C5C' } }}
          >
            Download Excel
          </Button>
        )}
      </Box>

      {/* Table */}
      {reportData.length > 0 && (
        <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ borderBottom: '2px solid #E1E1E1' }}>
                <TableCell sx={{ fontWeight: 'bold', color: '#4A4A4A', fontSize: '12px' }}>
                  Provider Name
                </TableCell>
                {aggregationLevel !== 'provider' && (
                  <TableCell sx={{ fontWeight: 'bold', color: '#4A4A4A', fontSize: '12px' }}>
                    Facility Name
                  </TableCell>
                )}
                <TableCell sx={{ fontWeight: 'bold', color: '#4A4A4A', fontSize: '12px' }}>
                  Pending
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#4A4A4A', fontSize: '12px' }}>
                  Enrolled
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#4A4A4A', fontSize: '12px' }}>
                  Waitlisted
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 'bold',
                    color: '#4A4A4A',
                    fontSize: '12px',
                    bgcolor: 'rgba(54, 117, 117, 0.1)'
                  }}
                >
                  Active / Capacity
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {aggregatedData.map((row, index) => (
                <TableRow
                  key={index}
                  sx={{
                    bgcolor: getRowColor(row.totalActiveSeats, row.totalAllocatedCapacity),
                    borderBottom: '1px solid #E1E1E1'
                  }}
                >
                  <TableCell sx={{ color: '#4A4A4A', fontSize: '12px' }}>
                    {row.providerName}
                  </TableCell>
                  {aggregationLevel !== 'provider' && (
                    <TableCell sx={{ color: '#4A4A4A', fontSize: '12px' }}>
                      {row.facilityName}
                    </TableCell>
                  )}
                  <TableCell sx={{ color: '#4A4A4A', fontSize: '12px' }}>
                    {row.countPendingProcessing}
                  </TableCell>
                  <TableCell sx={{ color: '#4A4A4A', fontSize: '12px' }}>
                    {row.countEnrolled}
                  </TableCell>
                  <TableCell sx={{ color: '#4A4A4A', fontSize: '12px' }}>
                    {row.countWaitlisted}
                  </TableCell>
                  <TableCell
                    sx={{
                      color: '#4A4A4A',
                      fontSize: '12px',
                      bgcolor: 'rgba(54, 117, 117, 0.1)'
                    }}
                  >
                    {row.totalActiveSeats} / {row.totalAllocatedCapacity}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  )
}
